/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

import React, { useState } from "react";
import { useTheme } from "../../theme/ThemeContext";

const OPERATORS = [
  { label: "is one of", value: "IN" },
  { label: "is not one of", value: "NOT IN" },
];

/**
 * Props:
 *   value            — { column, operator, values } | null  (the read_filter)
 *   onChange         — (readFilter|null) => void
 *   availableColumns — array of { name, data_type }
 */
export default function ReadFilterBuilder({ value, onChange, availableColumns = [] }) {
  const { theme } = useTheme();
  const [chipInput, setChipInput] = useState("");

  const column = value?.column || "";
  const operator = value?.operator || "IN";
  const values = value?.values || [];

  const emit = (patch) => onChange({ column, operator, values, ...patch });

  function addChip() {
    const v = chipInput.trim();
    if (v && !values.includes(v)) emit({ values: [...values, v] });
    setChipInput("");
  }
  function removeChip(v) { emit({ values: values.filter(x => x !== v) }); }

  const opLabel = OPERATORS.find(o => o.value === operator)?.label || "is one of";
  const selectStyle = {
    borderRadius: theme.buttonRadius, border: `1px solid ${theme.borderColor || "#dee2e6"}`,
    padding: "4px 8px", fontSize: 13, background: "#fff", minWidth: 140,
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select aria-label="Filter column" className="form-select" style={selectStyle}
          value={column} onChange={e => emit({ column: e.target.value })}>
          <option value="">Column...</option>
          {availableColumns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>

        <select aria-label="Filter operator" className="form-select" style={selectStyle}
          value={operator} onChange={e => emit({ operator: e.target.value })} disabled={!column}>
          {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        {(value && (column || values.length)) ? (
          <button type="button" aria-label="Clear filter"
            onClick={() => { onChange(null); setChipInput(""); }}
            style={{ background: "none", border: "none", color: "#dc3545", cursor: "pointer", fontSize: 13 }}>
            <i className="bi bi-x-circle me-1" /> Clear filter
          </button>
        ) : null}
      </div>

      {column && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 4 }}>
            {values.map(v => (
              <span key={v} style={{ background: theme.primary, color: "#fff", borderRadius: 12, padding: "2px 8px", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                {v}<span style={{ cursor: "pointer", fontWeight: 700 }} onClick={() => removeChip(v)}>×</span>
              </span>
            ))}
          </div>
          <input aria-label="Filter value" className="form-control" style={{ ...selectStyle, minWidth: 200 }}
            value={chipInput} placeholder="type a value and press Enter"
            onChange={e => setChipInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addChip(); } }} />
        </div>
      )}

      {column && values.length > 0 && (
        <div style={{ fontSize: 12, color: "#636979", marginTop: 6 }}>
          Show rows where <strong>{column}</strong> {opLabel}: {values.join(", ")}.
        </div>
      )}
    </div>
  );
}
